import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Search, Plus, LayoutDashboard, Trophy, BarChart3, Settings,
  Bell, Users, FileText
} from "lucide-react";

/* ─── Data ─── */
const QUICK_ACTIONS = [
  { icon: Plus, label: "Create New Activation", shortcut: "C", href: "/configurator", lime: true },
  { icon: LayoutDashboard, label: "Go to Dashboard", shortcut: "G D", href: "/dashboard" },
  { icon: Trophy, label: "Go to Tournaments", shortcut: "G T", href: "/dashboard" },
  { icon: BarChart3, label: "Go to Analytics", shortcut: "G A", href: "/dashboard/report" },
  { icon: Settings, label: "Open Settings", shortcut: "G S", href: "/dashboard/settings" },
  { icon: Bell, label: "View Notifications", shortcut: "G N", href: "/dashboard/notifications" },
];

const RECENT = [
  { icon: Trophy, label: "Valorant Championship", type: "Tournament" },
  { icon: Users, label: "Team Alpha", type: "Team" },
  { icon: FileText, label: "Player Management", type: "Page" },
];

interface SearchResult {
  icon: React.ElementType;
  label: string;
  meta?: string;
  type: string;
  section: string;
  href: string;
  status?: string;
}

const SEARCH_DATA: SearchResult[] = [
  { icon: Trophy, label: "Valorant Championship", meta: "256 players", type: "Tournament", section: "Tournaments", href: "/tournament/valorant-championship", status: "LIVE" },
  { icon: Trophy, label: "Valorant Community Cup", meta: "Draft", type: "Tournament", section: "Tournaments", href: "/tournament/community-cup" },
  { icon: Trophy, label: "Free Fire Open Cup", meta: "128 players", type: "Tournament", section: "Tournaments", href: "/tournament/freefire-open" },
  { icon: Users, label: "ValentinaBR", meta: "Team Alpha", type: "Player", section: "Players", href: "/profile/valentinabr" },
  { icon: Users, label: "valkyrieX", meta: "Free Agent", type: "Player", section: "Players", href: "/profile/valkyriex" },
  { icon: FileText, label: "Valorant Championship — Bracket", meta: "", type: "Page", section: "Pages", href: "/tournament/valorant-championship" },
  { icon: FileText, label: "Valorant Championship — Analytics", meta: "", type: "Page", section: "Pages", href: "/dashboard/report" },
  { icon: BarChart3, label: "Analytics Dashboard", meta: "", type: "Page", section: "Pages", href: "/dashboard/report" },
  { icon: Settings, label: "Settings", meta: "", type: "Page", section: "Pages", href: "/dashboard/settings" },
  { icon: Bell, label: "Notifications", meta: "", type: "Page", section: "Pages", href: "/dashboard/notifications" },
  { icon: Users, label: "PlayerOne", meta: "Team Alpha", type: "Player", section: "Players", href: "/profile/playerone" },
  { icon: Users, label: "N1tro", meta: "Phoenix Rising", type: "Player", section: "Players", href: "/profile/n1tro" },
];

function navigate(href: string) {
  window.location.href = href;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const results = query.trim()
    ? SEARCH_DATA.filter((r) => r.label.toLowerCase().includes(query.toLowerCase()))
    : [];

  const grouped: Record<string, SearchResult[]> = {};
  for (const r of results) {
    const bucket = grouped[r.section] ?? [];
    grouped[r.section] = bucket;
    bucket.push(r);
  }

  const flatResults = Object.values(grouped).flat();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      focusTimeoutRef.current = setTimeout(() => inputRef.current?.focus(), 50);
    }
    return () => clearTimeout(focusTimeoutRef.current);
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const totalItems = query.trim() ? flatResults.length : QUICK_ACTIONS.length + RECENT.length;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, totalItems - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (query.trim() && flatResults[activeIndex]) {
          setOpen(false);
          navigate(flatResults[activeIndex].href);
        } else if (!query.trim() && activeIndex < QUICK_ACTIONS.length) {
          const action = QUICK_ACTIONS[activeIndex];
          if (action) { setOpen(false); navigate(action.href); }
        }
      }
    },
    [query, activeIndex, flatResults]
  );

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]"
        onClick={() => setOpen(false)}
        style={{ backgroundColor: "var(--surface-overlay)", backdropFilter: "blur(4px)" }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: -10 }}
          transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="w-[90vw] max-w-[580px] max-h-[480px] bg-card rounded-xl shadow-2xl overflow-hidden flex flex-col"
          style={{ borderRadius: 12, boxShadow: "var(--shadow-modal)" }}
        >
          {/* Search Input */}
          <div className="flex items-center h-14 px-5 gap-3 bg-background">
            <Search size={18} className="text-primary shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
              onKeyDown={handleKeyDown}
              placeholder="Search tournaments, players, settings..."
              className="flex-1 bg-transparent text-foreground placeholder-muted-foreground outline-none"
              style={{ fontFamily: "var(--font-body)", fontSize: 16, caretColor: "var(--primary)" }}
            />
            <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0 font-mono" style={{ fontSize: 10 }}>
              ESC
            </span>
          </div>

          <div className="h-px bg-border" />

          {/* Content */}
          <div className="flex-1 overflow-y-auto py-2" style={{ scrollbarWidth: "thin" }}>
            {!query.trim() ? (
              <>
                <div className="px-4 py-2">
                  <p className="text-muted-foreground font-mono uppercase mb-1 px-2" style={{ fontSize: 10 }}>Quick Actions</p>
                  {QUICK_ACTIONS.map((action, i) => (
                    <a
                      key={action.label}
                      href={action.href}
                      className="flex items-center gap-3 h-11 px-2 rounded-lg transition-colors"
                      style={{
                        backgroundColor: activeIndex === i ? "var(--muted)" : "transparent",
                        borderLeft: activeIndex === i ? "2px solid var(--primary)" : "2px solid transparent" }}
                      onMouseEnter={() => setActiveIndex(i)}
                    >
                      <action.icon size={16} className={action.lime ? "text-primary" : "text-muted-foreground"} />
                      <span className="text-foreground flex-1" style={{ fontFamily: "var(--font-body)", fontSize: 14 }}>{action.label}</span>
                      <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono" style={{ fontSize: 10 }}>
                        {action.shortcut}
                      </span>
                    </a>
                  ))}
                </div>

                <div className="px-4 py-2">
                  <p className="text-muted-foreground font-mono uppercase mb-1 px-2" style={{ fontSize: 10 }}>Recent</p>
                  {RECENT.map((item, i) => {
                    const idx = QUICK_ACTIONS.length + i;
                    return (
                      <div
                        key={item.label}
                        className="flex items-center gap-3 h-11 px-2 rounded-lg transition-colors cursor-pointer"
                        style={{
                          backgroundColor: activeIndex === idx ? "var(--muted)" : "transparent",
                          borderLeft: activeIndex === idx ? "2px solid var(--primary)" : "2px solid transparent" }}
                        onMouseEnter={() => setActiveIndex(idx)}
                      >
                        <item.icon size={16} className="text-muted-foreground" />
                        <span className="text-foreground flex-1" style={{ fontFamily: "var(--font-body)", fontSize: 14 }}>{item.label}</span>
                        <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono" style={{ fontSize: 10 }}>
                          {item.type}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : results.length > 0 ? (
              <>
                {Object.entries(grouped).map(([section, items]) => (
                  <div key={section} className="px-4 py-2">
                    <p className="text-muted-foreground font-mono uppercase mb-1 px-2" style={{ fontSize: 10 }}>{section}</p>
                    {items.map((item) => {
                      const idx = flatResults.indexOf(item);
                      return (
                        <a
                          key={item.label + item.type}
                          href={item.href}
                          className="flex items-center gap-3 h-11 px-2 rounded-lg transition-colors"
                          style={{
                            backgroundColor: activeIndex === idx ? "var(--muted)" : "transparent",
                            borderLeft: activeIndex === idx ? "2px solid var(--primary)" : "2px solid transparent" }}
                          onMouseEnter={() => setActiveIndex(idx)}
                        >
                          <item.icon size={16} className="text-primary" />
                          <span className="text-foreground flex-1" style={{ fontFamily: "var(--font-body)", fontSize: 14 }}>{item.label}</span>
                          {item.status && (
                            <span className="px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground font-mono" style={{ fontSize: 9 }}>
                              {item.status}
                            </span>
                          )}
                          {item.meta && !item.status && (
                            <span className="text-muted-foreground" style={{ fontFamily: "var(--font-body)", fontSize: 12 }}>{item.meta}</span>
                          )}
                          <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono" style={{ fontSize: 10 }}>
                            {item.type}
                          </span>
                        </a>
                      );
                    })}
                  </div>
                ))}
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground" style={{ fontFamily: "var(--font-body)", fontSize: 14 }}>No results for "{query}"</p>
                <p className="text-muted-foreground mt-1 opacity-60" style={{ fontFamily: "var(--font-body)", fontSize: 13 }}>Try a different search term</p>
              </div>
            )}
          </div>

          <div className="h-px bg-border" />
          <div className="flex items-center justify-center py-2 gap-4">
            {["↑↓ Navigate", "↵ Open", "⌘K Close"].map((hint) => (
              <span key={hint} className="text-muted-foreground font-mono" style={{ fontSize: 10 }}>{hint}</span>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
